import type { User } from '@mend/types/user';
import type { AutocompleteRenderInputParams, TextFieldProps } from '@mui/material';
import type { BaseAutocompleteInputProps } from './Autocomplete.types';

import { temporaryColors } from '#lib/mui.ts';

type GetTextFieldPropsOptions<Multiple extends boolean | undefined = undefined> = Pick<
  BaseAutocompleteInputProps,
  'inputRequired'
> & {
  multiple: Multiple;
  params: AutocompleteRenderInputParams;
};

/**
 * Dynamically generate the required attributes for <TextField />.
 *
 * @see https://stackoverflow.com/questions/62645466/how-to-make-autocomplete-field-of-material-ui-required#comment129083754_64537120
 * @see https://github.com/mui/material-ui/issues/20840#issuecomment-659582972
 */
export const getTextFieldProps = <Multiple extends boolean | undefined = undefined>({
  params,
  multiple,
  inputRequired,
}: GetTextFieldPropsOptions<Multiple>): Partial<TextFieldProps> => ({
  ...params,
  required: multiple ? undefined : inputRequired,
  InputLabelProps: {
    ...params.InputLabelProps,
    required: multiple ? inputRequired : undefined,
  },
});

/**
 * The colour an option row states a user's role in. Shared by every autocomplete that renders one,
 * so two pickers listing the same person can't disagree about what colour a Provider is.
 *
 * Accepts `null` for an option that stands for no user at all, like the Unassigned sentinel.
 */
export function getRoleColor(role: User['role'] | null): string | undefined {
  switch (role) {
    case 'Provider':
      return temporaryColors.pink;
    case 'Provider Admin':
      return temporaryColors.orange;
    case 'Staff':
      return temporaryColors.green;
    case 'Org Admin':
      return temporaryColors.cyan;
    case 'Patient':
      return temporaryColors.yellow;
    case 'Ward':
      return temporaryColors.purple;
    default:
      return undefined;
  }
}
